import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Eye, 
  Tag, 
  Share2, 
  ArrowLeft,
  Heart,
  User,
  Quote,
  X,
  ChevronRight,
  ChevronLeft,
  ZoomIn,
  Newspaper,
  Megaphone,
  Trophy,
  Palette,
  Music,
  GraduationCap,
  Users as UsersIcon,
  Building2,
  Briefcase,
  Code,
  Lightbulb,
  Radio,
  Star,
  Award,
  Flag,
  Globe,
  Book,
  Camera,
  Video,
  MessageCircle,
  Mic,
  Zap,
  Rocket,
  Target
} from 'lucide-react';
import { useNews } from '../../hooks/useNews';
import { supabase } from '../../lib/supabase';
import type { NewsArticleWithRelations, NewsImage } from '../../types/news';

const NewsArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<NewsArticleWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<NewsImage | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  const { fetchArticleBySlug, incrementViews } = useNews();

  // Mapeamento de categorias para ícones
  const categoryIcons: { [key: string]: React.ElementType } = {
    noticias: Newspaper,
    destaques: Megaphone,
    desporto: Trophy,
    arte: Palette,
    musica: Music,
    educacao: GraduationCap,
    comunidade: UsersIcon,
    escola: Building2,
    carreira: Briefcase,
    tecnologia: Code,
    projetos: Lightbulb,
    eventos: Radio
  };

  // Mapeamento de tags para ícones
  const tagIcons: { [key: string]: React.ElementType } = {
    destaque: Star,
    premiado: Award,
    nacional: Flag,
    internacional: Globe,
    literatura: Book,
    fotografia: Camera,
    video: Video,
    entrevista: MessageCircle,
    podcast: Mic,
    inovacao: Zap,
    ciencia: Rocket,
    competicao: Target
  };

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      console.log('Carregando artigo com slug:', slug);
      const data = await fetchArticleBySlug(slug);
      
      if (!data) {
        console.error('Nenhum dado retornado para o artigo');
        return;
      }
      
      if (data.images && data.images.length > 0) {
        console.log('Processando URLs das imagens:', data.images);
        const processedImages = data.images.map((image: NewsImage) => {
          if (!image.url) {
            console.warn('Imagem sem URL:', image);
            return media;
          }
          
          if (image.url.startsWith('http')) {
            console.log('URL já é pública:', image.url);
            return image;
          }
          
          try {
            const { data: { publicUrl } } = supabase.storage
              .from('news')
              .getPublicUrl(image.url);
            
            console.log('URL pública gerada para imagem:', publicUrl);
            return {
              ...image,
              url: publicUrl
            };
          } catch (error) {
            console.error('Erro ao gerar URL pública para imagem:', error);
            return image;
          }
        });

        data.images = processedImages.sort((a, b) => (a.position || 0) - (b.position || 0));
      }

      if (data.cover_image && !data.cover_image.startsWith('http')) {
        try {
          const { data: { publicUrl } } = supabase.storage
            .from('news')
            .getPublicUrl(data.cover_image);
          data.cover_image = publicUrl;
        } catch (error) {
          console.error('Erro ao gerar URL pública para capa:', error);
        }
      }
      
      setArticle(data);
      await incrementViews(data.id);
    } catch (error) {
      console.error('Erro ao carregar artigo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextImage = () => {
    if (article?.media) {
      setCurrentImageIndex((prev) => 
        prev === article.media!.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handlePrevImage = () => {
    if (article?.media) {
      setCurrentImageIndex((prev) => 
        prev === 0 ? article.media!.length - 1 : prev - 1
      );
    }
  };

  // Função para obter ícone baseado na categoria
  const getThematicIcon = (index: number) => {
    if (!article?.category?.slug) return null;
    
    const baseIcon = categoryIcons[article?.category?.slug];
    if (!baseIcon) return null;

    // Rotacionar entre variações do ícone baseado no índice
    const variations = [
      { scale: 1, rotate: 0, opacity: 0.3 },
      { scale: 0.8, rotate: 45, opacity: 0.2 },
      { scale: 1.2, rotate: -45, opacity: 0.4 },
      { scale: 0.9, rotate: 90, opacity: 0.25 }
    ];

    const variation = variations[index % variations.length];
    
    return (
      <motion.div
        initial={false}
        animate={{
          scale: variation.scale,
          rotate: variation.rotate,
          opacity: variation.opacity
        }}
        transition={{ duration: 0.5 }}
        className="absolute -left-12 top-1/2 transform -translate-y-1/2"
      >
        {React.createElement(baseIcon, { className: "w-8 h-8 text-primary" })}
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gradient-to-br from-background to-background/80">
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="w-16 h-16 relative"
        >
          <div className="absolute inset-0 rounded-xl bg-primary/20 animate-pulse" />
          <div className="absolute inset-2 rounded-lg bg-primary/30 animate-pulse delay-75" />
          <div className="absolute inset-4 rounded-md bg-primary/40 animate-pulse delay-150" />
        </motion.div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen pt-16 flex items-center justify-center bg-gradient-to-br from-background to-background/80">
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center space-y-4"
        >
          <h1 className="text-2xl font-bold">Artigo não encontrado</h1>
          <Link 
            to="/news" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 rounded-xl transition-all duration-300 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] hover:shadow-[inset_0_2px_2px_rgba(255,255,255,0.2)]"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar às notícias
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-br from-background to-background/80 relative">
      {/* Grid Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 grid-background" />
        <div className="absolute inset-0 grid-overlay" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-transparent to-background/80" />
      </div>

      {/* Cabeçalho */}
      <div className="relative h-[70vh] overflow-hidden">
        <motion.div 
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: article.cover_image ? `url(${article.cover_image})` : undefined,
            backgroundPosition: 'center 30%',
            filter: 'brightness(0.5)',
          }}
        />
        
        <div className="absolute inset-0 flex flex-col justify-end p-8 bg-gradient-to-t from-background via-background/70 to-transparent">
          <motion.div 
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto w-full space-y-6"
          >
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar às notícias
            </Link>

            <div className="flex items-center gap-3">
              {article.category && (
                <motion.span 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="px-4 py-2 bg-primary/20 text-primary rounded-xl font-semibold backdrop-blur-sm flex items-center gap-2"
                >
                  {categoryIcons[article.category.slug] && React.createElement(categoryIcons[article.category.slug], { className: "w-4 h-4" })}
                  {article.category.name}
                </motion.span>
              )}
              {article.is_breaking && (
                <motion.span 
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="px-4 py-2 bg-destructive/20 text-destructive rounded-xl font-semibold backdrop-blur-sm animate-pulse"
                >
                  Última Hora
                </motion.span>
              )}
            </div>

            <motion.h1 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-5xl md:text-6xl font-bold leading-tight tracking-tight font-sans"
            >
              {article.title}
            </motion.h1>
            
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap items-center gap-6 text-sm text-gray-300 font-light"
            >
              <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
                <User className="w-4 h-4" />
                {article.author?.name || 'Anónimo'}
              </span>
              <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
                <Calendar className="w-4 h-4" />
                {new Date(article.published_at || '').toLocaleDateString('pt-PT')}
              </span>
              <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
                <Clock className="w-4 h-4" />
                {article.reading_time || '5 min'}
              </span>
              <span className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg">
                <Eye className="w-4 h-4" />
                {article.views_count} visualizações
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Elementos Decorativos Flutuantes */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0]
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/4 left-[15%] w-32 h-32 bg-primary/5 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              y: [0, 20, 0],
              rotate: [0, -5, 0]
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/3 right-[15%] w-48 h-48 bg-blue-500/5 rounded-full blur-3xl"
          />
        </div>

        {/* Seção de Resumo Destacada */}
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, type: "spring" }}
          className="mb-16"
        >
          <div className="relative p-8 bg-gradient-to-br from-primary/10 to-transparent rounded-3xl">
            <div className="absolute inset-0 bg-[url('/patterns/dots.svg')] opacity-5" />
            <div className="relative space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-1 bg-primary/50 rounded-full" />
                <span className="text-primary/80 font-semibold uppercase tracking-wider text-sm">
                  Destaques da Notícia
                </span>
              </div>
              <div className="text-2xl text-gray-200 leading-relaxed space-y-6 font-light">
                {article.content.split('\n\n').slice(0, 3).map((paragraph, index) => {
                  // Extrair apenas a primeira frase de cada parágrafo
                  const firstSentence = paragraph.split('.')[0] + '.';
                  if (index === 0) {
                    return (
                      <p key={index} className="text-primary/90 font-normal tracking-wide">
                        {firstSentence}
                      </p>
                    );
                  }
                  return (
                    <p key={index} className="text-xl text-gray-300 leading-relaxed font-light tracking-wide">
                      {firstSentence}
                    </p>
                  );
                })}
              </div>
              <div className="pt-4 flex items-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Tempo de leitura: {article.reading_time || '5 min'}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Publicado: {new Date(article.published_at || '').toLocaleDateString('pt-PT')}
                </span>
              </div>
            </div>
            <div className="absolute bottom-2 right-2 w-32 h-32 bg-gradient-to-tl from-primary/10 to-transparent rounded-full blur-3xl" />
          </div>
        </motion.div>

        {/* Galeria de Imagens */}
        {article.media && article.media.length > 0 ? (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mb-12 space-y-8"
          >
            <div className="flex items-center justify-between">
              <motion.h2 
                initial={{ x: -20 }}
                animate={{ x: 0 }}
                className="text-3xl font-bold relative"
              >
                <span className="text-primary">
                  Galeria de Imagens
                </span>
                <motion.div
                  className="absolute -bottom-2 left-0 h-1 bg-primary/50"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                />
              </motion.h2>
              <div className="flex items-center gap-2 md:gap-4">
                <div className="px-2 md:px-4 py-1 md:py-2 bg-white/5 backdrop-blur-lg rounded-xl border border-primary/20">
                  <span className="text-xs md:text-sm font-medium text-primary whitespace-nowrap">
                    {currentImageIndex + 1} / {article.media.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <motion.button
                    whileHover={{ scale: 1.05, x: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handlePrevImage}
                    className="p-2 md:p-3 rounded-xl bg-gradient-to-br from-white/10 to-white/5 hover:from-primary/20 hover:to-primary/5 border border-white/10 transition-all duration-300 group"
                  >
                    <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 group-hover:text-primary transition-colors" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05, x: 2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleNextImage}
                    className="p-2 md:p-3 rounded-xl bg-gradient-to-br from-white/10 to-white/5 hover:from-primary/20 hover:to-primary/5 border border-white/10 transition-all duration-300 group"
                  >
                    <ChevronRight className="w-4 h-4 md:w-5 md:h-5 group-hover:text-primary transition-colors" />
                  </motion.button>
                </div>
              </div>
            </div>
            
            {/* Carrossel 3D Avançado */}
            <div className="relative h-[600px] perspective-[3000px] group">
              {/* Efeito de Fundo Animado */}
              <motion.div
                className="absolute inset-0 rounded-3xl overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.5 }}
                transition={{ delay: 0.3 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-purple-500/10 to-blue-500/20" />
                <motion.div
                  className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]"
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </motion.div>

              {/* Container Principal */}
              <div className="relative w-full h-full">
                {article.media.map((image, index) => (
                  <motion.div
                    key={image.id}
                    className="absolute inset-0 w-full h-full"
                    initial={false}
                    animate={{
                      opacity: index === currentImageIndex ? 1 : 0,
                      scale: index === currentImageIndex ? 1 : 0.8,
                      rotateY: index === currentImageIndex ? 0 : (index < currentImageIndex ? 45 : -45),
                      z: index === currentImageIndex ? 0 : -200
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 100,
                      damping: 20,
                      mass: 1
                    }}
                    style={{
                      transformStyle: 'preserve-3d',
                      pointerEvents: index === currentImageIndex ? 'auto' : 'none'
                    }}
                  >
                    <motion.div 
                      className="relative w-full h-full rounded-3xl overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.3)] group/card"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.4 }}
                    >
                      {/* Efeito de Reflexo Dinâmico */}
                      <motion.div
                        className="absolute inset-0 bg-[linear-gradient(105deg,transparent_40%,rgba(255,255,255,0.2)_45%,rgba(255,255,255,0.3)_50%,rgba(255,255,255,0.2)_55%,transparent_60%)] opacity-0 group-hover/card:opacity-100 transition-opacity duration-500"
                        animate={{
                          x: ['-100%', '200%']
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "linear",
                          repeatDelay: 1
                        }}
                      />

                      {/* Container Principal do Carrossel */}
                      <div className="relative overflow-hidden rounded-3xl bg-black/20">
                        {/* Container da Imagem */}
                        <motion.div 
                          className="relative flex items-center justify-center bg-black/40"
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.7 }}
                        >
                          {/* Imagem */}
                          <motion.img
                            src={image.url}
                            alt={image.alt || 'Imagem da notícia'}
                            className="w-full h-full object-contain py-2 xs:py-3 md:py-4"
                            style={{ maxHeight: '80vh' }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = 'https://placehold.co/1920x1080?text=Erro+ao+carregar+imagem';
                            }}
                            initial={{ scale: 1.1, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.7 }}
                          />

                          {/* Overlay com Gradiente Dinâmico */}
                          <motion.div 
                            className="absolute inset-0 pointer-events-none"
                            initial={{ opacity: 0.5 }}
                            whileHover={{ opacity: 0.3 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />
                          </motion.div>
                        </motion.div>
                      </div>


                      {/* Botões de Ação Flutuantes */}
                      <motion.div 
                        className="absolute top-4 right-4 flex items-center gap-2"
                        initial={{ opacity: 0 }}
                        whileHover={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setSelectedImage(image)}
                          className="p-4 rounded-xl bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 shadow-lg transition-all duration-300 group"
                        >
                          <ZoomIn className="w-5 h-5 group-hover:text-primary transition-colors" />
                        </motion.button>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>

              {/* Controles de Navegação Melhorados */}
              <div className="absolute -bottom-4 xs:-bottom-6 md:-bottom-8 left-1/2 -translate-x-1/2 flex flex-wrap justify-center items-center gap-1 xs:gap-2 max-w-full px-2">
                {article.media.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className="group relative p-1 xs:p-1.5 md:p-2"
                    whileHover={{ scale: 1.2 }}
                  >
                    <div className={`
                      w-1.5 h-1.5 xs:w-2 xs:h-2 rounded-full transition-all duration-500
                      ${index === currentImageIndex
                        ? 'bg-gradient-to-r from-primary to-purple-500 scale-150'
                        : 'bg-white/30 group-hover:bg-white/50'
                      }
                    `} />
                    {index === currentImageIndex && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute -inset-2 rounded-full bg-primary/20 blur-sm"
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30
                        }}
                      />
                    )}
                  </motion.button>
                ))}
              </div>

              {/* Botões de Navegação Laterais */}
              <div className="absolute inset-y-0 left-4 right-4 flex items-center justify-between pointer-events-none">
                <motion.button
                  className="pointer-events-auto p-1 xs:p-1.5 md:p-2 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  whileHover={{ scale: 1.1, x: -2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handlePrevImage}
                >
                  <ChevronLeft className="w-4 h-4 xs:w-5 xs:h-5 md:w-6 md:h-6 text-white/90" />
                </motion.button>
                <motion.button
                  className="pointer-events-auto p-1 xs:p-1.5 md:p-2 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  whileHover={{ scale: 1.1, x: 2 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleNextImage}
                >
                  <ChevronRight className="w-4 h-4 xs:w-5 xs:h-5 md:w-6 md:h-6 text-white/90" />
                </motion.button>
              </div>
            </div>

            {/* Miniaturas em Grade com Efeitos */}
            <div className="grid grid-cols-3 xs:grid-cols-4 md:grid-cols-6 gap-1 xs:gap-2 md:gap-3 pt-2 xs:pt-3 md:pt-4">
              {article.media.map((image, index) => (
                <motion.button
                  key={image.id}
                  whileHover={{
                    scale: 1.08,
                    y: -5,
                    transition: { duration: 0.3 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`
                    relative aspect-video rounded-xl overflow-hidden group
                    ${index === currentImageIndex 
                      ? 'ring-2 ring-primary shadow-lg shadow-primary/20' 
                      : 'hover:shadow-xl hover:shadow-primary/10'
                    }
                    transition-all duration-300 transform-gpu backdrop-blur-md bg-white/5
                  `}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30 mix-blend-overlay opacity-70" />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-purple-500/20 mix-blend-color" />
                  <img
                    src={image.url}
                    alt={image.alt || 'Miniatura'}
                    className={`
                      w-full h-full object-cover transform transition-all duration-700
                      grayscale group-hover:grayscale-0
                      ${index === currentImageIndex 
                        ? 'scale-100 brightness-100' 
                        : 'scale-110 brightness-75 group-hover:brightness-90'
                      }
                    `}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://placehold.co/480x270?text=Erro';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-overlay" />
                  {index === currentImageIndex && (
                    <motion.div
                      layoutId="activeImageIndicator"
                      className="absolute inset-0 border-2 border-primary rounded-xl"
                      transition={{ duration: 0.3 }}
                    >
                      <div className="absolute inset-0 bg-primary/10 animate-pulse mix-blend-overlay" />
                    </motion.div>
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mb-12 p-8 bg-white/5 rounded-2xl text-center border border-white/10"
          >
            <p className="text-gray-400">
              Este artigo ainda não tem imagens na galeria.
            </p>
          </motion.div>
        )}

        {/* Conteúdo do Artigo */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="prose prose-invert max-w-none"
        >
          {/* Conteúdo Principal */}
          {article.content.split('\n\n').map((paragraph, index) => {
            // Verificar se o parágrafo é igual ao excerpt
            if (paragraph === article.excerpt) {
              return null;
            }

            // Se for um dos três primeiros parágrafos, mostrar apenas o resto do texto após a primeira frase
            if (index < 3) {
              const sentences = paragraph.split('.');
              if (sentences.length > 1) {
                // Remover a primeira frase e juntar o resto
                const remainingText = sentences.slice(1).join('.').trim();
                if (!remainingText) return null;
                paragraph = remainingText + '.';
              } else {
                return null;
              }
            }

            const isAlternate = index % 3 === 0;
            const isQuote = index % 5 === 2;
            const hasImage = index % 4 === 1;

            if (hasImage && article.media && article.media[index % article.media.length]) {
              const image = article.media[index % article.media.length];
              const position = index % 2 === 0 ? 'left' : 'right';
              
              return (
                <motion.div
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  className="my-16 md:my-24"
                >
                  <div className={`flex flex-col md:flex-row gap-8 md:gap-12 items-start ${position === 'right' ? 'md:flex-row-reverse' : ''}`}>
                    <div className="relative w-full md:w-1/3 group">
                      <motion.div
                        className="relative rounded-xl overflow-hidden"
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3 }}
                      >
                        <motion.img
                          src={image.url}
                          alt={image.alt || ''}
                          className="w-full aspect-[4/3] object-cover rounded-xl"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = 'https://placehold.co/800x600?text=Erro+ao+carregar+imagem';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <motion.button
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setSelectedImage(image)}
                          className="absolute top-2 right-2 p-2 rounded-lg bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 opacity-0 group-hover:opacity-100 transition-all duration-300"
                        >
                          <ZoomIn className="w-4 h-4" />
                        </motion.button>
                      </motion.div>
                    </div>
                    <div className="flex-1">
                      <p className="text-xl text-gray-300 leading-relaxed font-light tracking-wide">
                        {paragraph}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            }

            if (isQuote) {
              return (
                <motion.div
                  key={index}
                  initial={{ scale: 0.95, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="my-24 px-8 py-16 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 rounded-2xl text-center relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-[url('/patterns/circuit.svg')] opacity-5 animate-pulse" />
                  <motion.div
                    animate={{ rotate: [0, 5, 0] }}
                    transition={{ duration: 10, repeat: Infinity }}
                    className="absolute -left-8 -top-8 text-primary/10"
                  >
                    <Quote size={96} />
                  </motion.div>
                  <p className="text-3xl italic text-gray-200 relative z-10 font-extralight leading-relaxed tracking-wide">
                    "{paragraph}"
                  </p>
                  <motion.div
                    animate={{ rotate: [0, -5, 0] }}
                    transition={{ duration: 12, repeat: Infinity }}
                    className="absolute -right-8 -bottom-8 text-primary/10 transform rotate-180"
                  >
                    <Quote size={96} />
                  </motion.div>
                </motion.div>
              );
            }

            if (isAlternate) {
              return (
                <motion.div
                  key={index}
                  initial={{ x: -50, opacity: 0 }}
                  whileInView={{ x: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="my-24 flex flex-col md:flex-row gap-12 items-center relative"
                >
                  {getThematicIcon(index)}
                  <div className="w-full md:w-1/3 aspect-square rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('/patterns/hexagons.svg')] opacity-5" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-8xl font-bold text-primary/20 font-sans">
                        {(index + 1).toString().padStart(2, '0')}
                      </div>
                    </div>
                  </div>
                  <div className="w-full md:w-2/3">
                    <p className="text-xl text-gray-300 leading-relaxed font-light tracking-wide">
                      {paragraph}
                    </p>
                  </div>
                </motion.div>
              );
            }

            return (
              <motion.div
                key={index}
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="my-16 relative group"
              >
                {getThematicIcon(index)}
                <div className="absolute left-0 top-0 w-1 h-0 group-hover:h-full bg-gradient-to-b from-primary via-primary/50 to-transparent transition-all duration-500 rounded-full" />
                <div className="pl-8 py-6">
                  <p className="text-xl text-gray-300 leading-relaxed font-light tracking-wide">
                    {paragraph}
                  </p>
                </div>
                <div className="absolute bottom-0 left-0 w-0 group-hover:w-full h-[1px] bg-gradient-to-r from-primary/30 to-transparent transition-all duration-500" />
              </motion.div>
            );
          })}

          {/* Separador Final Melhorado */}
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="relative h-32 my-16"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  ease: "linear"
                }}
                className="w-12 h-12 rounded-full bg-primary/10 border border-primary/20 p-2"
              >
                <div className="w-full h-full rounded-full bg-primary/20 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        {/* Seção de Tags com novo estilo */}
        {article.tags && article.tags.length > 0 && (
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-12 pt-8"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-px flex-grow bg-gradient-to-r from-white/10 to-transparent" />
              <span className="text-sm text-gray-400 px-4 py-2 bg-white/5 rounded-full backdrop-blur-sm">
                Tópicos Relacionados
              </span>
              <div className="h-px flex-grow bg-gradient-to-l from-white/10 to-transparent" />
            </div>

            <div className="flex flex-wrap gap-3">
              {article.tags.map((tag) => (
                <motion.div
                  key={tag.id}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={`/news/archive?tag=${tag.slug}`}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-sm transition-all duration-300 border border-white/10 hover:border-primary/30 group"
                  >
                    {tagIcons[tag.slug] ? 
                      React.createElement(tagIcons[tag.slug], { className: "w-4 h-4 text-primary group-hover:scale-110 transition-transform" }) :
                      <Tag className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" />
                    }
                    <span className="group-hover:text-primary transition-colors">{tag.name}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Botões de Interação com novo estilo */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="flex items-center justify-between mt-12 p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm"
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsLiked(!isLiked)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-300 ${
                isLiked 
                  ? 'bg-primary/20 text-primary border border-primary/30' 
                  : 'bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20'
              }`}
            >
              <Heart 
                className={`w-5 h-5 transition-all duration-300 ${
                  isLiked 
                    ? 'fill-primary scale-110' 
                    : 'scale-100'
                }`}
              />
              <span className="font-medium">
                {isLiked ? 'Gostou' : 'Gostar'}
              </span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={async () => {
                try {
                  await navigator.share({
                    title: article.title,
                    text: article.excerpt || '',
                    url: window.location.href
                  });
                } catch {
                  await navigator.clipboard.writeText(window.location.href);
                  // Aqui você pode adicionar um toast de sucesso
                }
              }}
              className="flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-300 border border-white/10 hover:border-white/20"
            >
              <Share2 className="w-5 h-5" />
              <span className="font-medium">Partilhar</span>
            </motion.button>
          </div>

          {article.category && (
            <Link
              to={`/news/archive?category=${article.category.slug}`}
              className="flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-all duration-300 border border-primary/30 group"
            >
              <span className="font-medium">Mais em {article.category.name}</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </motion.div>
      </div>

      {/* Modal de Visualização */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4 backdrop-blur-lg"
            onClick={() => setSelectedImage(null)}
          >
            <motion.button
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-all"
            >
              <X className="w-6 h-6" />
            </motion.button>
            
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-6xl w-full"
            >
              <img
                src={selectedImage.url}
                alt={selectedImage.alt || 'Imagem em tamanho grande'}
                className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = 'https://placehold.co/1920x1080?text=Erro+ao+carregar+imagem';
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NewsArticle;
